import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
// @mui
import {
  Box,
  Card,
  Chip,
  Stack,
  Button,
  Collapse,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';
// components
import Iconify from '../../../components/Iconify';

/**
 * Per-lecture hand-in editor. Deliberately mirrors LectureQuizBuilder: same
 * collapsed-by-default card, same `active` master switch, and it writes to
 * `lectures.<i>.assignment`.
 *
 * Creating an assignment here is what puts the green "Submit" button on the
 * student's lecture rail, and what gives the instructor portal something to
 * grade. Per-cohort carve-outs are managed from the Batches section, exactly
 * like the quiz.
 */
export default function LectureAssignmentBuilder({
  lectureIndex,
}: {
  lectureIndex: number;
}) {
  const { watch, setValue } = useFormContext();
  const base = `lectures.${lectureIndex}.assignment`;
  const assignment = watch(base) || {};
  const [open, setOpen] = useState(false);

  // Read as `active !== false` so an assignment saved before this flag existed
  // still counts as on.
  const isActive = assignment?.active !== false;
  const exists = !!(assignment && Object.keys(assignment).length);

  const set = (field: string, value: any) =>
    setValue(`${base}.${field}`, value, { shouldDirty: true });

  const create = () => {
    setValue(
      base,
      {
        title: '',
        title_ar: '',
        instructions: '',
        instructions_ar: '',
        attachmentUrl: '',
        dueDate: '',
        maxScore: 100,
        passingScore: 50,
        allowResubmit: true,
        active: true,
        excludedBatches: [],
      },
      { shouldDirty: true }
    );
    setOpen(true);
  };

  const removeAssignment = () => {
    setValue(base, undefined, { shouldDirty: true });
    setOpen(false);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ flexWrap: 'wrap', gap: 0.5 }}
      >
        <Button
          size="small"
          color="inherit"
          onClick={() => (exists ? setOpen((o) => !o) : create())}
          startIcon={
            <Iconify
              icon={
                exists
                  ? open
                    ? 'eva:arrow-ios-upward-fill'
                    : 'eva:arrow-ios-downward-fill'
                  : 'eva:plus-fill'
              }
            />
          }
        >
          {exists ? 'Assignment (hand-in)' : 'Add an assignment'}
        </Button>

        {exists && (
          <>
            <Chip
              size="small"
              label={isActive ? 'On' : 'Off'}
              color={isActive ? 'success' : 'default'}
              variant={isActive ? 'filled' : 'outlined'}
            />
            {!!assignment?.dueDate && (
              <Chip
                size="small"
                variant="outlined"
                label={`Due ${String(assignment.dueDate).slice(0, 10)}`}
              />
            )}
            <Chip
              size="small"
              variant="outlined"
              label={`${assignment?.maxScore ?? 100} marks`}
            />
          </>
        )}
      </Stack>

      {exists && (
        <Collapse in={open}>
          <Card sx={{ p: 2, mt: 1.5, bgcolor: 'background.neutral' }}>
            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => set('active', e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    Active — students can submit
                  </Typography>
                }
              />

              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                }}
              >
                <TextField
                  size="small"
                  label="Title"
                  value={assignment?.title || ''}
                  onChange={(e) => set('title', e.target.value)}
                />
                <TextField
                  size="small"
                  label="Title (Arabic)"
                  inputProps={{ dir: 'rtl' }}
                  value={assignment?.title_ar || ''}
                  onChange={(e) => set('title_ar', e.target.value)}
                />
              </Box>

              <TextField
                size="small"
                multiline
                minRows={3}
                label="Instructions"
                value={assignment?.instructions || ''}
                onChange={(e) => set('instructions', e.target.value)}
              />
              <TextField
                size="small"
                multiline
                minRows={3}
                label="Instructions (Arabic)"
                inputProps={{ dir: 'rtl' }}
                value={assignment?.instructions_ar || ''}
                onChange={(e) => set('instructions_ar', e.target.value)}
              />

              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                }}
              >
                <TextField
                  size="small"
                  type="date"
                  label="Due date"
                  InputLabelProps={{ shrink: true }}
                  helperText="Blank = open-ended"
                  value={String(assignment?.dueDate || '').slice(0, 10)}
                  onChange={(e) => set('dueDate', e.target.value)}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Max score"
                  value={assignment?.maxScore ?? 100}
                  onChange={(e) => set('maxScore', Number(e.target.value))}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Passing score"
                  value={assignment?.passingScore ?? 50}
                  onChange={(e) => set('passingScore', Number(e.target.value))}
                />
              </Box>

              <TextField
                size="small"
                label="Brief / starter file URL (optional)"
                helperText="A file the student downloads before submitting"
                value={assignment?.attachmentUrl || ''}
                onChange={(e) => set('attachmentUrl', e.target.value)}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={assignment?.allowResubmit !== false}
                    onChange={(e) => set('allowResubmit', e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    Allow students to replace their submission
                  </Typography>
                }
              />

              <Stack direction="row" justifyContent="flex-end">
                <Button
                  size="small"
                  color="error"
                  onClick={removeAssignment}
                  startIcon={<Iconify icon="eva:trash-2-outline" />}
                >
                  Remove assignment
                </Button>
              </Stack>
            </Stack>
          </Card>
        </Collapse>
      )}
    </Box>
  );
}
